"use client";

import { useMemo } from "react";
import type { AtlasSearchProcess } from "./integration";
import { buildThoughtStreamSteps } from "./integration";

export interface GhostStreamItem {
  id: string;
  label: string;
  completed: boolean;
  active?: boolean;
}

/**
 * Derives condensed, safe "ghost" lines from Atlas thought stream for the processing overlay.
 * Reuses existing Atlas data – no duplicate feed. Last N items as meta labels only.
 */
export function getGhostStreamItems(
  process: AtlasSearchProcess | null,
  maxItems = 6
): GhostStreamItem[] {
  if (!process) {
    return [{ id: "idle", label: "Preparing…", completed: false, active: true }];
  }
  const steps = buildThoughtStreamSteps(process);
  const phaseLabels: Record<string, string> = {
    read: "Read & parse",
    plan: "Planning navigation",
    reason: "Reasoning",
    act: "Orienting",
    respond: "Respond",
  };
  const items: GhostStreamItem[] = steps.slice(0, maxItems).map((s, i) => ({
    id: `${s.phase}-${i}`,
    label: s.label || phaseLabels[s.phase] || s.phase,
    completed: !!s.completed,
    active: !!s.active,
  }));
  if (process.message?.trim()) {
    const msg = process.message.length > 48 ? process.message.slice(0, 45) + "…" : process.message;
    if (!items.some((x) => x.label === msg)) {
      items.push({ id: "msg", label: msg, completed: false, active: true });
    }
  }
  return items.slice(-maxItems);
}

/**
 * Hook: returns ghost stream items from current Atlas process (for overlay).
 * Use same process/state as Atlas panel – no second feed.
 */
export function useAtlasGhostStream(
  atlasProcess: AtlasSearchProcess | null,
  _atlasHistory?: unknown[],
  maxItems = 6
): GhostStreamItem[] {
  return useMemo(
    () => getGhostStreamItems(atlasProcess, maxItems),
    [atlasProcess?.status, atlasProcess?.message, maxItems]
  );
}
